import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../App';
import './schoolYearsAdmin.css';
import { formatDateVNDate } from '../../../utils/formatDate';

function authHeader() { const t = localStorage.getItem('token'); return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' }; }

export default function SchoolYearsAdmin() {
  const { user } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL || '';
  const [schoolYears, setSchoolYears] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);

  // create form
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  // year edit/delete modal state
  const [yearModalOpen, setYearModalOpen] = useState(false);
  const [editingYear, setEditingYear] = useState(null);
  const [yearName, setYearName] = useState('');
  const [yearStart, setYearStart] = useState('');
  const [yearEnd, setYearEnd] = useState('');
  const [yearIsDefault, setYearIsDefault] = useState(false);
  const [savingYear, setSavingYear] = useState(false);
  // top-level default selector
  const [selectedDefaultYear, setSelectedDefaultYear] = useState('');
  // schools create/edit modal state
  const [schoolModalOpen, setSchoolModalOpen] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);
  const [schoolName, setSchoolName] = useState('');
  const [schoolStatus, setSchoolStatus] = useState('active');
  const [savingSchool, setSavingSchool] = useState(false);
  const [schoolAddress, setSchoolAddress] = useState('');
  const [schoolPhone, setSchoolPhone] = useState('');
  const [schoolEmail, setSchoolEmail] = useState('');
  const [schoolWebsite, setSchoolWebsite] = useState('');

  useEffect(() => {
    if (!user) return;
    if (user.role !== 'Admin') return;
    loadAll();
  }, [user]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [syRes, scRes] = await Promise.all([
        fetch(`${apiUrl}/api/admin/school-years`, { headers: authHeader() }),
        fetch(`${apiUrl}/api/admin/schools`, { headers: authHeader() })
      ]);
      const syJ = syRes && syRes.ok ? await syRes.json() : { schoolYears: [] };
      const scJ = scRes && scRes.ok ? await scRes.json() : { schools: [] };
      const yrs = syJ.schoolYears || [];
      setSchoolYears(yrs);
      // set selected default to current default year id (if any)
      const d = yrs.find(y => y.is_default || y.isDefault || y.is_default === 1 || y.isDefault === 1);
      setSelectedDefaultYear(d && d.id ? String(d.id) : '');
      setSchools(scJ.schools || []);
    } catch (e) { console.error('loadAll failed', e); }
    setLoading(false);
  };

  const createYear = async () => {
    setError(null);
    if (!name) { setError('Name required'); return; }
    setCreating(true);
    try {
      const res = await fetch(`${apiUrl}/api/admin/school-years`, { method: 'POST', headers: authHeader(), body: JSON.stringify({ name, start_date: startDate || null, end_date: endDate || null, is_default: isDefault ? 1 : 0 }) });
      if (!res.ok) { const j = await res.json().catch(()=>null); throw new Error((j && j.message) ? j.message : 'Server error'); }
      setName(''); setStartDate(''); setEndDate(''); setIsDefault(false);
      await loadAll();
    } catch (e) { console.error('createYear failed', e); setError(e.message || String(e)); }
    setCreating(false);
  };

  const setDefault = async (id) => {
    try {
      const res = await fetch(`${apiUrl}/api/admin/school-years/${id}/default`, { method: 'PUT', headers: authHeader() });
      if (!res.ok) throw new Error('Failed');
      await loadAll();
    } catch (e) { console.error('setDefault failed', e); }
  };

  const openYearModal = (y) => {
    if (y) {
      setEditingYear(y);
      setYearName(y.name || '');
      setYearStart(y.start_date || '');
      setYearEnd(y.end_date || '');
      setYearIsDefault(!!(y.is_default || y.isDefault || y.is_default === 1 || y.isDefault === 1));
    } else {
      setEditingYear(null);
      setYearName('');
      setYearStart('');
      setYearEnd('');
      setYearIsDefault(false);
    }
    setYearModalOpen(true);
  };

  const saveYear = async () => {
    if (!yearName || !String(yearName).trim()) return alert('Year name required');
    setSavingYear(true);
    try {
      const payload = { name: yearName.trim(), start_date: yearStart || null, end_date: yearEnd || null, is_default: 0 };
      let res;
      let createdId = null;
      if (editingYear && editingYear.id) {
        res = await fetch(`${apiUrl}/api/admin/school-years/${editingYear.id}`, { method: 'PUT', headers: authHeader(), body: JSON.stringify(payload) });
        createdId = editingYear.id;
      } else {
        res = await fetch(`${apiUrl}/api/admin/school-years`, { method: 'POST', headers: authHeader(), body: JSON.stringify(payload) });
        if (res.ok) {
          const j = await res.json().catch(() => null);
          if (j && j.schoolYear && j.schoolYear.id) createdId = j.schoolYear.id;
        }
      }
      if (!res.ok) { const txt = await res.text().catch(()=>null); throw new Error(txt || 'Failed'); }
      await loadAll();
      setYearModalOpen(false);
    } catch (e) { console.error('saveYear failed', e); alert('Failed to save year'); }
    setSavingYear(false);
  };

  const deleteYear = async (id) => {
    if (!window.confirm('Delete this school year?')) return;
    try {
      const res = await fetch(`${apiUrl}/api/admin/school-years/${id}`, { method: 'DELETE', headers: authHeader() });
      if (!res.ok) { const txt = await res.text().catch(()=>null); throw new Error(txt || 'Failed'); }
      await loadAll();
    } catch (e) { console.error('deleteYear failed', e); alert('Failed to delete year'); }
  };

  const updateSchoolYearForSchool = async (schoolId, yearId) => {
    try {
      const payload = { year_id: yearId || null };
      const res = await fetch(`${apiUrl}/api/admin/schools/${schoolId}`, { method: 'PUT', headers: authHeader(), body: JSON.stringify(payload) });
      if (!res.ok) { const txt = await res.text().catch(()=>null); throw new Error(txt || 'Failed'); }
      await loadAll();
    } catch (e) { console.error('updateSchoolYearForSchool failed', e); alert('Failed to update school'); }
  };

  const openSchoolModal = (s) => {
    if (s) {
      setEditingSchool(s);
      setSchoolName(s.school_name || s.name || '');
      setSchoolStatus(s.status || 'active');
      setSchoolAddress(s.address || '');
      setSchoolPhone(s.phone || '');
      setSchoolEmail(s.email || '');
      setSchoolWebsite(s.website || '');
    } else {
      setEditingSchool(null);
      setSchoolName('');
      setSchoolStatus('active');
      setSchoolAddress('');
      setSchoolPhone('');
      setSchoolEmail('');
      setSchoolWebsite('');
    }
    setSchoolModalOpen(true);
  };

  const saveSchool = async () => {
    if (!schoolName || !String(schoolName).trim()) return alert('School name required');
    setSavingSchool(true);
    try {
    const payload = {
      school_name: schoolName.trim(),
      status: schoolStatus || 'active',
      address: schoolAddress && String(schoolAddress).trim() ? String(schoolAddress).trim() : null,
      phone: schoolPhone && String(schoolPhone).trim() ? String(schoolPhone).trim() : null,
      email: schoolEmail && String(schoolEmail).trim() ? String(schoolEmail).trim() : null,
      website: schoolWebsite && String(schoolWebsite).trim() ? String(schoolWebsite).trim() : null
    };
      let res;
      if (editingSchool && editingSchool.id) {
        res = await fetch(`${apiUrl}/api/admin/schools/${editingSchool.id}`, { method: 'PUT', headers: authHeader(), body: JSON.stringify(payload) });
      } else {
        res = await fetch(`${apiUrl}/api/admin/schools`, { method: 'POST', headers: authHeader(), body: JSON.stringify(payload) });
      }
      if (!res.ok) { const txt = await res.text().catch(()=>null); throw new Error(txt || 'Failed'); }
      await loadAll();
      setSchoolModalOpen(false);
    } catch (e) { console.error('saveSchool failed', e); alert('Failed to save school'); }
    setSavingSchool(false);
  };

  // helpers to display / parse dates as dd/MM/yyyy in the form while keeping ISO for payload
  const parseDateInput = (val) => {
    if (!val) return '';
    // allow dd/mm/yyyy or dd-mm-yyyy -> convert to yyyy-mm-dd
    const dmy = String(val).trim().match(/^(\d{2})[\/\-](\d{2})[\/\-](\d{4})$/);
    if (dmy) {
      const dd = dmy[1], mm = dmy[2], yyyy = dmy[3];
      return `${yyyy}-${mm}-${dd}`;
    }
    // if already ISO yyyy-mm-dd
    const ymd = String(val).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymd) return val;
    // try Date parse fallback
    const dt = new Date(val);
    if (!isNaN(dt)) {
      const yy = dt.getFullYear();
      const mm = String(dt.getMonth()+1).padStart(2,'0');
      const dd = String(dt.getDate()).padStart(2,'0');
      return `${yy}-${mm}-${dd}`;
    }
    return val;
  };

  const deleteSchool = async (id) => {
    if (!window.confirm('Delete this school?')) return;
    try {
      const res = await fetch(`${apiUrl}/api/admin/schools/${id}`, { method: 'DELETE', headers: authHeader() });
      if (!res.ok) { const txt = await res.text().catch(()=>null); throw new Error(txt || 'Failed'); }
      await loadAll();
    } catch (e) { console.error('deleteSchool failed', e); alert('Failed to delete school'); }
  };

  if (!user || user.role !== 'Admin') return <div className="muted">Access denied</div>;

  return (
    <div style={{ padding: 12 }}>
      <h2>School years management</h2>
      {/* <div style={{ marginTop: 8, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
        <label style={{ fontWeight: 600 }}>Default year:</label>
        <select value={selectedDefaultYear || ''} onChange={e => setSelectedDefaultYear(e.target.value || '')}>
          <option value="">-- No default --</option>
          {(schoolYears || []).map(y => (<option key={y.id} value={String(y.id)}>{y.name}</option>))}
        </select>
        <button onClick={async () => {
          if (!selectedDefaultYear) return alert('Please select a year to set as default');
          try { await setDefault(selectedDefaultYear); } catch (e) { console.error(e); }
        }}>Save default</button>
      </div> */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
        <div style={{ width: '100%' }}>
          <div>
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4>School years</h4>
                <div>
                  <button onClick={() => openYearModal(null)}>Add year</button>
                </div>
              </div>
              {loading ? <div className="muted">Loading…</div> : (
                <div style={{ maxHeight: 520, overflow: 'auto' }}>
                  <table className="data-table" style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Start</th>
                        <th>End</th>
                        <th>Default</th>
                        <th className="col-actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(schoolYears || []).map(sy => (
                        <tr key={sy.id}>
                          <td>{sy.name}</td>
                          <td>{formatDateVNDate(sy.start_date) || ''}</td>
                          <td>{formatDateVNDate(sy.end_date) || ''}</td>
                          <td>{sy.is_default ? 'Yes' : ''}</td>
                          <td className="col-actions">
                            {!sy.is_default && <button onClick={() => setDefault(sy.id)}>Set default</button>}
                            <button style={{ marginLeft: 8 }} onClick={() => openYearModal(sy)}>Edit</button>
                            <button style={{ marginLeft: 8 }} onClick={async () => { await deleteYear(sy.id); }}>Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

  <div style={{ width: '100%' }}>
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4>Assign school year to schools</h4>
              <div>
                <button onClick={() => openSchoolModal(null)}>Add new school</button>
              </div>
            </div>
            {loading ? <div className="muted">Loading…</div> : (
              <div style={{ maxHeight: 520, overflow: 'auto' }}>
                <table className="data-table" style={{ width: '100%' }}>
                  <thead><tr><th>School</th><th>Year</th><th className="col-actions">Action</th></tr></thead>
                  <tbody>
                    {(schools || []).map(s => (
                      <tr key={s.id}>
                        <td>{s.school_name || s.name || `#${s.id}`}</td>
                        <td>{(schoolYears || []).find(y => y.id === s.year_id)?.name || '-- No year --'}</td>
                        <td className="col-actions">
                          <button style={{ marginLeft: 8 }} onClick={() => openSchoolModal(s)}>Edit</button>
                          <button style={{ marginLeft: 8 }} onClick={async () => { await deleteSchool(s.id); }}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* School create / edit modal */}
      {schoolModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }} onClick={() => setSchoolModalOpen(false)}>
          <div style={{ width: 520, background: '#fff', borderRadius: 8, padding: 16 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 style={{ margin: 0 }}>{editingSchool ? 'Edit school' : 'Create new school'}</h4>
              <button onClick={() => setSchoolModalOpen(false)}>✕</button>
            </div>
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#444' }}>School name</label>
                <input style={{ width: '100%' }} value={schoolName} onChange={e => setSchoolName(e.target.value)} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#444' }}>Status</label>
                <select value={schoolStatus} onChange={e => setSchoolStatus(e.target.value)}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#444' }}>Address</label>
                <input style={{ width: '100%' }} value={schoolAddress} onChange={e => setSchoolAddress(e.target.value)} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#444' }}>Phone</label>
                <input style={{ width: '100%' }} value={schoolPhone} onChange={e => setSchoolPhone(e.target.value)} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#444' }}>Email</label>
                <input style={{ width: '100%' }} value={schoolEmail} onChange={e => setSchoolEmail(e.target.value)} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#444' }}>Website</label>
                <input style={{ width: '100%' }} value={schoolWebsite} onChange={e => setSchoolWebsite(e.target.value)} />
              </div>
              {/* Assigned year is read-only in the school create/edit modal per policy */}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button onClick={() => setSchoolModalOpen(false)} className="btn">Cancel</button>
                <button onClick={saveSchool} className="btn btn-primary" disabled={savingSchool}>{savingSchool ? 'Saving…' : (editingSchool ? 'Save' : 'Create')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Year create / edit modal */}
      {yearModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }} onClick={() => setYearModalOpen(false)}>
          <div style={{ width: 520, background: '#fff', borderRadius: 8, padding: 16 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 style={{ margin: 0 }}>{editingYear ? 'Edit year' : 'Create new year'}</h4>
              <button onClick={() => setYearModalOpen(false)}>✕</button>
            </div>
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#444' }}>Name</label>
                <input style={{ width: '100%' }} value={yearName} onChange={e => setYearName(e.target.value)} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 12, color: '#444' }}>Start date</label>
                  <input type="date" value={yearStart} onChange={e => setYearStart(e.target.value)} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 12, color: '#444' }}>End date</label>
                  <input type="date" value={yearEnd} onChange={e => setYearEnd(e.target.value)} />
                </div>
              </div>
              {/* default selection moved to page-level control */}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button onClick={() => setYearModalOpen(false)} className="btn">Cancel</button>
                <button onClick={saveYear} className="btn btn-primary" disabled={savingYear}>{savingYear ? 'Saving…' : (editingYear ? 'Save' : 'Create')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
